// Jornada do visitante (WF-4800): registra, SÓ no navegador, por onde o
// visitante andou no site antes de virar lead — origem (UTM/referrer), seções
// vistas, CTAs clicados, nº de visitas, tempo ativo e dispositivo.
//
// IMPORTANTE (LGPD):
// - Nada sai do navegador antes do lead. A jornada só vai junto com o envio do
//   formulário/trial (sendLead.js) ou como dynamic variable da Dika, e o texto
//   de consentimento fica acima do botão de envio.
// - Sem PII: rótulos que pareçam e-mail/telefone/CPF são descartados; URLs vão
//   sem query string (só as UTMs são lidas); texto digitado nunca é capturado.
// - Respeita Global Privacy Control / Do Not Track: com o sinal ligado, não grava
//   e apaga o que já houver.
//
// Formato do resumo = colunas da tabela CRM `jornada_no_site` (satélite do
// Cliente). Os nomes com duplo "_" (data_da_1__visita, n__de_visitas,
// tempo_total__s) são os nomes reais das colunas.

const KEY = 'dk_journey';
const LP = 'digitalk-lp';
const MAX_EVENTS = 200;
const MAX_FULL_BYTES = 20000;
const SESSION_GAP_MS = 30 * 60 * 1000;
const TTL_MS = 30 * 24 * 60 * 60 * 1000;
const SECTION_DWELL_MS = 2000;
const TICK_MS = 5000;
const IDLE_MS = 60 * 1000;
const INTERNAL_HOST = /(^|\.)digitalk\.com\.br$/i;

const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const CPF = /\b\d{3}\.?\d{3}\.?\d{3}-?\d{2}\b/;
const PHONE = /(\+?\d[\s().-]*){8,}/;

let state = null;
let started = false;
let lastInput = 0;

const now = () => Date.now();
const visible = () => document.visibilityState === 'visible';

function optedOut() {
  try {
    return navigator.globalPrivacyControl === true
      || navigator.doNotTrack === '1' || window.doNotTrack === '1';
  } catch (_) { return false; }
}

function emptyState() {
  return { v: 1, visits: 0, activeMs: 0, events: [], first: null, last: 0 };
}

function read() {
  try {
    const raw = window.localStorage.getItem(KEY);
    const s = raw ? JSON.parse(raw) : null;
    if (s && s.v === 1 && now() - (s.last || 0) < TTL_MS) return s;
  } catch (_) { /* storage bloqueado: segue só em memória */ }
  return null;
}

// Toda mudança passa por aqui: relê o storage antes de aplicar, para que várias
// abas abertas não apaguem os eventos/tempo umas das outras.
function commit(mutate) {
  state = read() || state || emptyState();
  mutate(state);
  if (state.events.length > MAX_EVENTS) state.events.splice(0, state.events.length - MAX_EVENTS);
  try { window.localStorage.setItem(KEY, JSON.stringify(state)); } catch (_) { /* cheio/bloqueado */ }
}

// UTMs: só e-mail/CPF derrubam o valor (campanhas costumam ter datas/IDs longos).
function cleanUtm(v) {
  const s = String(v == null ? '' : v).trim().slice(0, 120);
  return !s || EMAIL.test(s) || CPF.test(s) ? '' : s;
}

// Rótulos (CTA/seção/página): qualquer coisa que pareça e-mail, CPF ou telefone sai.
function cleanLabel(v, max = 80) {
  const s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim().slice(0, max);
  return !s || EMAIL.test(s) || CPF.test(s) || PHONE.test(s) ? '' : s;
}

function pushTo(s, k, raw) {
  const v = k === 'visit' ? raw : cleanLabel(raw);
  const last = s.events[s.events.length - 1];
  // seção/página repetida em sequência (rolar pra cima e pra baixo, recarregar) não conta de novo
  if ((k === 'section' || k === 'page') && last && last.k === k && last.v === v) return;
  s.events.push(v ? { t: now(), k, v } : { t: now(), k });
  s.last = now();
}

function push(k, v) {
  if (!started) return;
  commit((s) => pushTo(s, k, v));
}

function device() {
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet/i.test(ua) || (/Android/i.test(ua) && !/Mobile/i.test(ua))) return 'tablet';
  if (/Mobi|iPhone|Android/i.test(ua)) return 'mobile';
  return 'desktop';
}

// Domínio de origem externo; navegar entre apex/www/digitalk-lp é interno.
function referrerDomain() {
  try {
    if (!document.referrer) return '';
    const host = new URL(document.referrer).hostname;
    return host === window.location.hostname || INTERNAL_HOST.test(host) ? '' : host;
  } catch (_) { return ''; }
}

function startVisit(s) {
  const q = new URLSearchParams(window.location.search);
  const utm = {
    utm_source: cleanUtm(q.get('utm_source')),
    utm_medium: cleanUtm(q.get('utm_medium')),
    utm_campaign: cleanUtm(q.get('utm_campaign')),
  };
  const ref = referrerDomain();
  const path = cleanLabel(window.location.pathname, 120) || '/';
  if (!s.first) s.first = { t: now(), ...utm, referrer: ref, landing: path };
  s.visits += 1;
  const origin = utm.utm_source
    ? [utm.utm_source, utm.utm_medium].filter(Boolean).join(' / ')
    : (ref || 'direto');
  pushTo(s, 'visit', `${origin}${utm.utm_campaign ? ` · ${utm.utm_campaign}` : ''} · ${path}`);
}

// Nova visita quando o visitante volta depois de 30 min (recarregando ou
// voltando a uma aba que ficou aberta).
function maybeNewVisit() {
  commit((s) => { if (!s.visits || now() - (s.last || 0) > SESSION_GAP_MS) startVisit(s); });
}

function sectionName(el) {
  if (el.dataset && el.dataset.dkSection) return el.dataset.dkSection;
  if (el.id) return el.id;
  const cls = (el.className && typeof el.className === 'string') ? el.className.split(/\s+/)[0] : '';
  return cls;
}

function observeSections() {
  if (typeof IntersectionObserver === 'undefined') return () => {};
  const timers = new Map();
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      const el = en.target;
      // "vista" = a seção cruza a linha do meio da tela (rootMargin abaixo),
      // o que funciona também para seções mais altas que a viewport.
      if (en.isIntersecting) {
        if (!timers.has(el)) {
          timers.set(el, setTimeout(() => {
            timers.delete(el);
            if (visible()) push('section', sectionName(el));
          }, SECTION_DWELL_MS));
        }
      } else if (timers.has(el)) {
        clearTimeout(timers.get(el));
        timers.delete(el);
      }
    });
  }, { rootMargin: '-50% 0px -50% 0px', threshold: 0 });
  document.querySelectorAll('section, [data-dk-section]').forEach((el) => {
    if (sectionName(el) && !el.closest('.digi-panel')) io.observe(el);
  });
  return () => { io.disconnect(); timers.forEach(clearTimeout); };
}

// Tempo ativo = aba visível E alguém mexeu (scroll/tecla/mouse/toque) no último minuto.
function trackActiveTime() {
  let lastTick = now();
  lastInput = now();
  const onInput = () => { lastInput = now(); };
  const inputs = ['scroll', 'keydown', 'pointerdown', 'pointermove', 'touchstart'];
  inputs.forEach((e) => window.addEventListener(e, onInput, { passive: true }));
  const tick = () => {
    const t = now();
    const dt = Math.min(t - lastTick, TICK_MS * 2);
    lastTick = t;
    if (!visible() || t - lastInput > IDLE_MS) return;
    commit((s) => { s.activeMs += dt; s.last = t; });
  };
  const iv = setInterval(tick, TICK_MS);
  const onVis = () => {
    if (visible()) { lastTick = now(); lastInput = now(); maybeNewVisit(); } else tick();
  };
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('pagehide', tick);
  return () => {
    clearInterval(iv);
    inputs.forEach((e) => window.removeEventListener(e, onInput));
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('pagehide', tick);
  };
}

// Liga a captura (uma vez por carregamento de página). `page` = 'index' | 'vendas'.
export function startJourney(page = 'index') {
  if (started || typeof window === 'undefined') return () => {};
  if (optedOut()) {
    try { window.localStorage.removeItem(KEY); } catch (_) { /* ignora */ }
    return () => {};
  }
  started = true;
  maybeNewVisit();
  push('page', page);
  const stopSections = observeSections();
  const stopTime = trackActiveTime();
  return () => { stopSections(); stopTime(); started = false; };
}

// Eventos do track() que entram na jornada (rótulo legível, nunca PII).
const JOURNEY_EVENTS = {
  cta_click: (p) => p.label,
  digi_open: (p) => (p.intent ? `Digi (${p.intent})` : 'Abriu a Digi'),
  digi_choice: (p) => p.label,
  dika_open: () => 'Abriu a Dika',
  lead_captured: () => 'Enviou contato',
  trial_submitted: () => 'Pediu ambiente de teste',
};

export function recordJourney(event, params = {}) {
  const fn = JOURNEY_EVENTS[event];
  if (!fn) return;
  push('cta', fn(params) || event);
}

// Ordem da primeira vez que cada item apareceu (sem repetir).
function firstSeen(list, sep, max) {
  const out = [];
  list.forEach((v) => { if (v && !out.includes(v)) out.push(v); });
  return out.join(sep).slice(0, max);
}

const bytes = (s) => (typeof TextEncoder !== 'undefined' ? new TextEncoder().encode(s).length : s.length * 2);

// JSON sempre válido: corta os eventos mais antigos até caber.
function fullJson(events, maxBytes) {
  if (maxBytes <= 0) return '';
  let evs = events.slice();
  let json = JSON.stringify(evs);
  while (bytes(json) > maxBytes && evs.length) {
    evs = evs.slice(Math.max(1, Math.ceil(evs.length * 0.1)));
    json = JSON.stringify(evs);
  }
  return json;
}

// Resumo no formato das colunas de `jornada_no_site`. null = sem jornada
// (opt-out, storage bloqueado ou captura não iniciada).
// `maxFullBytes: 0` deixa `jornada_completa` vazia (canal Dika: só o resumo vai
// para a ElevenLabs).
export function getJourney({ canal = 'formulario', maxFullBytes = MAX_FULL_BYTES } = {}) {
  const s = read() || state;
  if (!s || !s.first) return null;
  const ev = s.events;
  return {
    lp: LP,
    canal_do_lead: canal,
    data_da_1__visita: new Date(s.first.t).toISOString(),
    data_do_lead: new Date().toISOString(),
    utm_source: s.first.utm_source || '',
    utm_medium: s.first.utm_medium || '',
    utm_campaign: s.first.utm_campaign || '',
    referrer: s.first.referrer || '',
    pagina_de_entrada: s.first.landing || '/',
    dispositivo: device(),
    secoes_vistas: firstSeen(ev.filter((e) => e.k === 'section').map((e) => e.v), ' > ', 500),
    ctas_clicados: firstSeen(ev.filter((e) => e.k === 'cta').map((e) => e.v), '; ', 500),
    n__de_visitas: s.visits,
    tempo_total__s: Math.round(s.activeMs / 1000),
    jornada_completa: fullJson(ev, maxFullBytes),
    consentimento: true,
  };
}

// Depois que o lead saiu com a jornada, recomeça do zero (computador
// compartilhado não herda a jornada de outra pessoa).
export function resetJourney() {
  state = emptyState();
  try { window.localStorage.removeItem(KEY); } catch (_) { /* ignora */ }
}
